/**
 * Form Validation System - 2026 Standard (Stripe/Linear)
 * 
 * Philosophy:
 * - Guide users, don't punish them
 * - Validate when it matters (blur, submit)
 * - One error at a time
 * - Clear recovery actions
 * - Calm, contextual feedback
 */

/* ═══════════════════════════════════════════════════════════
   VALIDATION RULES (Reusable)
   ═══════════════════════════════════════════════════════════ */

/**
 * Email validation
 * @param {string} email 
 * @returns {object} { valid: boolean, error: string }
 */
export function validateEmail(email) {
  if (!email || email.trim() === "") {
    return { valid: false, error: null }; // Empty is handled separately
  }

  const trimmed = email.trim();
  
  // Check for multiple @ symbols
  if ((trimmed.match(/@/g) || []).length !== 1) {
    return { 
      valid: false, 
      error: "Enter a valid email address (e.g. name@company.com)" 
    };
  }

  // Check for spaces
  if (trimmed.includes(" ")) {
    return { 
      valid: false, 
      error: "Email address cannot contain spaces" 
    };
  }

  // Standard email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { 
      valid: false, 
      error: "Enter a valid email address (e.g. name@company.com)" 
    };
  }

  return { valid: true, error: null };
}

/**
 * Phone validation (India-aware)
 * @param {string} phone 
 * @returns {object} { valid: boolean, error: string }
 */
export function validatePhone(phone) {
  if (!phone || phone.trim() === "") {
    return { valid: false, error: null }; // Empty is handled separately
  }

  const cleaned = phone.replace(/[\s\-\(\)]/g, "");
  
  // Remove +91 prefix if present
  const number = cleaned.startsWith("+91") ? cleaned.slice(3) : cleaned;
  
  // Must be 10 digits
  if (!/^\d{10}$/.test(number)) {
    return { 
      valid: false, 
      error: "Enter a valid 10-digit phone number" 
    };
  }

  return { valid: true, error: null };
}

/**
 * GSTIN validation (India)
 * @param {string} gstin 
 * @returns {object} { valid: boolean, error: string, warning: string }
 */
export function validateGSTIN(gstin) {
  if (!gstin || gstin.trim() === "") {
    return { 
      valid: true, // Optional field
      error: null,
      warning: "GSTIN is recommended for tax invoices"
    };
  }

  const trimmed = gstin.trim().toUpperCase();
  
  // Must be exactly 15 characters
  if (trimmed.length !== 15) {
    return { 
      valid: false, 
      error: "GSTIN must be exactly 15 characters",
      warning: null
    };
  }

  // Format: 22AAAAA0000A1Z5
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!gstinRegex.test(trimmed)) {
    return { 
      valid: false, 
      error: "Enter a valid GSTIN format",
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Pincode validation (India)
 * @param {string} pincode 
 * @returns {object} { valid: boolean, error: string }
 */
export function validatePincode(pincode) {
  if (!pincode || pincode.trim() === "") {
    return { valid: false, error: null }; // Empty is handled separately
  }

  const trimmed = pincode.trim();
  
  // Must be exactly 6 digits
  if (!/^\d{6}$/.test(trimmed)) {
    return { 
      valid: false, 
      error: "Pincode must be 6 digits" 
    };
  }

  return { valid: true, error: null };
}

/**
 * Required field validation
 * @param {any} value 
 * @param {string} fieldName 
 * @returns {object} { valid: boolean, error: string }
 */
export function validateRequired(value, fieldName = "This field") {
  if (value === null || value === undefined) {
    return { 
      valid: false, 
      error: `${fieldName} is required` 
    };
  }

  if (typeof value === "string" && value.trim() === "") {
    return { 
      valid: false, 
      error: `${fieldName} is required` 
    };
  }

  if (Array.isArray(value) && value.length === 0) {
    return { 
      valid: false, 
      error: `${fieldName} is required` 
    };
  }

  return { valid: true, error: null };
}

/**
 * Number validation (positive)
 * @param {number} value 
 * @param {string} fieldName 
 * @param {number} min 
 * @returns {object} { valid: boolean, error: string }
 */
export function validateNumber(value, fieldName = "Value", min = 0) {
  if (value === null || value === undefined || value === "") {
    return { 
      valid: false, 
      error: `${fieldName} is required` 
    };
  }

  const num = Number(value);
  
  if (isNaN(num)) {
    return { 
      valid: false, 
      error: `${fieldName} must be a number` 
    };
  }

  if (num < min) {
    return { 
      valid: false, 
      error: min === 0 
        ? `${fieldName} cannot be negative`
        : `${fieldName} must be at least ${min}`
    };
  }

  return { valid: true, error: null };
}

/**
 * Date validation
 * @param {string} date - ISO date string
 * @param {string} fieldName 
 * @returns {object} { valid: boolean, error: string }
 */
export function validateDate(date, fieldName = "Date") {
  if (!date || date.trim() === "") {
    return { 
      valid: false, 
      error: `${fieldName} is required` 
    };
  }

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return { 
      valid: false, 
      error: `${fieldName} is invalid` 
    };
  }

  return { valid: true, error: null };
}

/**
 * Date range validation (end >= start)
 * @param {string} startDate 
 * @param {string} endDate 
 * @param {string} startLabel 
 * @param {string} endLabel 
 * @returns {object} { valid: boolean, error: string }
 */
export function validateDateRange(startDate, endDate, startLabel = "Start date", endLabel = "End date") {
  if (!startDate || !endDate) {
    return { valid: true, error: null }; // Skip if either is empty
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (end < start) {
    return { 
      valid: false, 
      error: `${endLabel} cannot be before ${startLabel.toLowerCase()}` 
    };
  }

  return { valid: true, error: null };
}

/* ═══════════════════════════════════════════════════════════
   FORM VALIDATORS (Composite)
   ═══════════════════════════════════════════════════════════ */

/**
 * Validate client form
 * @param {object} client 
 * @param {object} options - { validateOnBlur: boolean }
 * @returns {object} { valid: boolean, errors: object, warnings: object }
 */
export function validateClientForm(client, options = {}) {
  const errors = {};
  const warnings = {};

  // Required: Client Name
  const nameValidation = validateRequired(client.name, "Client name");
  if (!nameValidation.valid) {
    errors.name = nameValidation.error;
  }

  // Required: Email (with format validation)
  if (!client.email || client.email.trim() === "") {
    errors.email = "Email is required";
  } else {
    const emailValidation = validateEmail(client.email);
    if (!emailValidation.valid) {
      errors.email = emailValidation.error;
    }
  }

  // Optional: Phone (validate if provided)
  if (client.phone && client.phone.trim() !== "") {
    const phoneValidation = validatePhone(client.phone);
    if (!phoneValidation.valid) {
      errors.phone = phoneValidation.error;
    }
  }

  // Optional: GSTIN (validate if provided, warn if missing)
  const gstinValidation = validateGSTIN(client.gstin);
  if (!gstinValidation.valid) {
    errors.gstin = gstinValidation.error;
  } else if (gstinValidation.warning && !options.validateOnBlur) {
    warnings.gstin = gstinValidation.warning;
  }

  // Required: City
  const cityValidation = validateRequired(client.city, "City");
  if (!cityValidation.valid) {
    errors.city = cityValidation.error;
  }

  // Required: State
  const stateValidation = validateRequired(client.state, "State");
  if (!stateValidation.valid) {
    errors.state = stateValidation.error;
  }

  // Optional: Pincode (validate if provided)
  if (client.pincode && client.pincode.trim() !== "") {
    const pincodeValidation = validatePincode(client.pincode);
    if (!pincodeValidation.valid) {
      errors.pincode = pincodeValidation.error;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate item/product form
 * @param {object} item 
 * @returns {object} { valid: boolean, errors: object }
 */
export function validateItemForm(item) {
  const errors = {};

  // Required: Item Name
  const nameValidation = validateRequired(item.name, "Item name");
  if (!nameValidation.valid) {
    errors.name = nameValidation.error;
  }

  // Required: Quantity (must be > 0)
  const quantityValidation = validateNumber(item.quantity, "Quantity", 1);
  if (!quantityValidation.valid) {
    errors.quantity = quantityValidation.error;
  }

  // Required: Rate (must be >= 0)
  const rateValidation = validateNumber(item.rate, "Rate", 0);
  if (!rateValidation.valid) {
    errors.rate = rateValidation.error;
  }

  // Optional: GST Rate (validate if provided)
  if (item.gstRate !== null && item.gstRate !== undefined && item.gstRate !== "") {
    const gstValidation = validateNumber(item.gstRate, "GST rate", 0);
    if (!gstValidation.valid) {
      errors.gstRate = gstValidation.error;
    } else if (Number(item.gstRate) > 100) {
      errors.gstRate = "GST rate cannot exceed 100%";
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate SMTP settings
 * @param {object} smtp 
 * @returns {object} { valid: boolean, errors: object }
 */
export function validateSMTPSettings(smtp) {
  const errors = {};

  if (!smtp.enabled) {
    return { valid: true, errors: {} }; // Skip validation if disabled
  }

  // Required: Host
  const hostValidation = validateRequired(smtp.smtp_host, "SMTP host");
  if (!hostValidation.valid) {
    errors.smtp_host = hostValidation.error;
  }

  // Required: Port
  const portValidation = validateNumber(smtp.smtp_port, "Port", 1);
  if (!portValidation.valid) {
    errors.smtp_port = portValidation.error;
  } else if (Number(smtp.smtp_port) > 65535) {
    errors.smtp_port = "Port must be between 1 and 65535";
  }

  // Required: Username (email)
  if (!smtp.smtp_user || smtp.smtp_user.trim() === "") {
    errors.smtp_user = "Email is required";
  } else {
    const emailValidation = validateEmail(smtp.smtp_user);
    if (!emailValidation.valid) {
      errors.smtp_user = emailValidation.error;
    }
  }

  // Required: Password
  const passwordValidation = validateRequired(smtp.smtp_password, "Password");
  if (!passwordValidation.valid) {
    errors.smtp_password = passwordValidation.error;
  }

  // Optional: From Email (validate if provided)
  if (smtp.from_email && smtp.from_email.trim() !== "") {
    const fromEmailValidation = validateEmail(smtp.from_email);
    if (!fromEmailValidation.valid) {
      errors.from_email = fromEmailValidation.error;
    }
  }

  // Optional: Reply-To Email (validate if provided)
  if (smtp.reply_to && smtp.reply_to.trim() !== "") {
    const replyToValidation = validateEmail(smtp.reply_to);
    if (!replyToValidation.valid) {
      errors.reply_to = replyToValidation.error;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/* ═══════════════════════════════════════════════════════════
   FIELD STATE HELPERS
   ═══════════════════════════════════════════════════════════ */

/**
 * Get field state for UI rendering
 * @param {string} error - Error message
 * @param {string} warning - Warning message
 * @param {boolean} touched - Has field been touched
 * @returns {object} { state: 'error'|'warning'|'neutral', message: string, className: string }
 */
export function getFieldState(error, warning, touched = true) {
  if (error && touched) {
    return {
      state: 'error',
      message: error,
      className: 'border-red-300 focus:border-red-500 focus:ring-red-500',
      helperClassName: 'text-red-600',
    };
  }

  if (warning && touched) {
    return {
      state: 'warning',
      message: warning,
      className: 'border-amber-300 focus:border-amber-500 focus:ring-amber-500',
      helperClassName: 'text-amber-600',
    };
  }

  return {
    state: 'neutral',
    message: null,
    className: 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-500',
    helperClassName: 'text-slate-500',
  };
}

/**
 * Get first error from errors object
 * @param {object} errors 
 * @returns {string|null}
 */
export function getFirstError(errors) {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }

  const firstKey = Object.keys(errors)[0];
  const error = errors[firstKey];
  
  return typeof error === 'string' ? error : error?.message || null;
}

/**
 * Focus first error field
 * @param {object} errors 
 */
export function focusFirstError(errors) {
  if (!errors || Object.keys(errors).length === 0) {
    return;
  }

  const firstKey = Object.keys(errors)[0];
  
  // Try to find and focus the field
  const element = 
    document.querySelector(`[name="${firstKey}"]`) ||
    document.querySelector(`#${firstKey}`) ||
    document.querySelector(`[data-field="${firstKey}"]`);

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    
    setTimeout(() => {
      if (element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "SELECT") {
        element.focus();
      }
    }, 300);
  }
}
